import { useMemo } from 'react'
import { useWidget } from '../../../../providers/widget'
import { useFetchWidget } from '../../../../hooks'
import { ExploreWidgetConfig, VisitorFilter } from '../../config'
import { useExplorer } from '../../providers'
import { useFetchProperties, withBrowsePriceField, stripHiddenRoomFilters } from '../../data'
import SortDropdown from './SortDropdown'
import { resolveSortOptions } from '../../sort'
import { boundsFiltering } from "../map/initialView"
import { CompareToggle } from "../../compare/CompareUi"
import { trailingSlashModeOf, withTrailingSlash } from '../../../../common/trailing-slash'

/**
 * Pieces shared between the legacy filter bar and the Searchbar Pro variant
 * (#853): the search-submit behavior (inline vs redirect-on-submit), the
 * matching-total from the shared properties query, and the secondary row
 * (Start Over / count / sort / map toggle) — identical in both variants.
 */

/**
 * Matching-total plus whether it has actually arrived.
 *
 * `useFilterTotals()` collapses the pair to a number with `?? 0`, which is
 * right for a count line that reads "0 homes" while loading — but WRONG for
 * the search button, which would flash "Search 0 homes" on every first paint
 * and after any query-key change that misses cache. Callers that render
 * the count as prose take this hook and hold the plain label until `hasData`.
 */
export function useFilterTotalsState(): { total: number; hasData: boolean } {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state } = useExplorer()

  const boundsString = boundsFiltering(state) && state.mapBounds
    ? `${state.mapBounds.west},${state.mapBounds.south},${state.mapBounds.east},${state.mapBounds.north}`
    : undefined

  const filtersWithBounds = useMemo(() => {
    const filters = { ...state.visitorFilters }
    if (boundsString) {
      filters.map_bounds = boundsString
    }
    return withBrowsePriceField(filters, options)
  }, [state.visitorFilters, boundsString, options])

  const { data } = useFetchProperties(feedId, filtersWithBounds, options.filter.limit, options.filter?.platform)
  const total = data?.pages?.[0]?.pagination?.totalItems
  return { total: total ?? 0, hasData: typeof total === 'number' }
}

export function useFilterTotals(): number {
  return useFilterTotalsState().total
}

/**
 * #934: hero/standalone layout (the toolkit module's refine="0" variant).
 * Hero implies redirect-on-submit: the bar is the whole widget, so there are
 * no on-page results to filter inline and every redirect-gated behavior
 * (submit navigation, auto-advance chains, CTA count) must treat hero as on.
 * All redirect gates go through isRedirectMode so the implication cannot be
 * missed at one call site.
 */
export function isHeroBar(options: ExploreWidgetConfig): boolean {
  return options.search?.heroBar === true
}

export function isRedirectMode(options: ExploreWidgetConfig): boolean {
  return options.search?.redirectOnSubmit === true || isHeroBar(options)
}

/**
 * @param overrides Filter values to merge over `state.visitorFilters` before
 *   building the redirect URL — for a caller that just dispatched a filter
 *   change and cannot rely on `state` reflecting it yet (`setFilter` is a
 *   reducer dispatch; the submit closure would otherwise read the PRE-patch
 *   state when both calls land in the same synchronous handler, e.g. a hero
 *   quick-filter chip tap, #1255). Ignored in inline mode, where `setFilter`
 *   applies them the normal way.
 */
export function useSearchSubmit() {
  const { options, feedId, widgetId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const listingsRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.listings

  return (overrides?: Partial<VisitorFilter>) => {
    if (isRedirectMode(options)) {
      // Standalone mode: navigate to the listings page (configured on the feed
      // via reverseProxy.routes.listings) with the current filter state
      // serialized into URL params.
      // #1848: the feed's preferred trailing-slash form, one hop to the page.
      const target = withTrailingSlash(listingsRoute || '/properties', trailingSlashModeOf(widgetData?.feed))
      const params = new URLSearchParams()
      const filters = overrides ? { ...state.visitorFilters, ...overrides } : state.visitorFilters
      // Hidden room filters must not ride the redirect URL either (#858).
      for (const [key, value] of Object.entries(stripHiddenRoomFilters(filters, options))) {
        if (value === undefined || value === null || value === '' || value === false) continue
        params.append(key, String(value))
      }
      const qs = params.toString()
      const url = qs ? `${target}${target.includes('?') ? '&' : '?'}${qs}` : target
      if (typeof window !== 'undefined') {
        window.location.href = url
      }
      return
    }

    // Inline mode (default): apply the filters to the current widget's query.
    setFilter(overrides ?? {})
  }
}

export function SecondaryRow({ totalItems, pro = false }: { totalItems: number; pro?: boolean }) {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { toggleMap, state } = useExplorer()

  if (options.showSecondaryFilters === false) return null

  // Module parity (#1102): with the count line off and neither sort nor map
  // toggle to show, the module never creates the results header at all
  // (initExplorerHeader early return, searchbar-pro.js:4608) — an empty row
  // would still reserve its min-height and margin. Sort visibility is the
  // EFFECTIVE one: SortDropdown also hides itself when fewer than two options
  // resolve (module Shortcode.php:799), and that must not leave an empty row.
  const sortVisible = (options.showSortDropdown ?? true) && resolveSortOptions(options.sort).length >= 2
  if ((options.showListingsCount ?? true) === false
    && !sortVisible
    && !options.showMap) return null

  // Pro variant (#924 / SBP-7): the module's results header — count as a
  // 16px/600 heading on the LEFT ("75 homes", the module's localized
  // "%s home(s)"), controls on the RIGHT (toolkit css:2433-2445 +
  // searchbar-pro.js:4496-4530). The map toggle is a widget feature with no
  // module slot; it joins the right-hand control group. Start Over is gone
  // from BOTH variants (r8 #937): the module has no reset-all in its results
  // view, and resetFilters stays reachable via the sheet's Clear all.
  if (pro) {
    return (
      <div className="hfse-form">
        <div className="hfse-sbp-results-header">
          {/* Count line is admin-toggleable (#1102, toolkit
              enable_explorer_count); the controls keep right via their own
              margin-left:auto, so the header needs no placeholder. */}
          {(options.showListingsCount ?? true) && (
            <div className="hfse-sbp-results-count" aria-live="polite">
              <strong>{totalItems}</strong> {totalItems === 1 ? 'home' : 'homes'}
            </div>
          )}
          <div className="hfse-sbp-results-controls">
            {(options.showSortDropdown ?? true) && <SortDropdown />}
            <CompareToggle />
            {options.showMap && (
              <button type="button" className="hk-button hfse-form-map-toggle-btn" onClick={toggleMap}>
                <span className="hk-icon hfse-form-map-toggle-btn-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </span>
                <span className="hfse-form-map-toggle-btn-text">
                  {state.mapVisible ? 'Hide Map' : 'Show Map'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="hfse-form">
      <div className="hfse-form-secondary-fields">
        {/* Kept (empty) so the center column stays centered under
            space-between — Start Over itself is gone (r8 #937). */}
        <div className="hfse-form-secondary-fields-left" />

        <div className="hfse-form-secondary-fields-center">
          <div className="hfse-form-meta">
            {(options.showListingsCount ?? true) && (
              <span className="hfse-form-property-count">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <path d="M12 3l10 9h-3v9h-6v-6H11v6H5v-9H2l10-9z" />
                </svg>
                <strong>{totalItems}</strong> Properties
              </span>
            )}
            {(options.showSortDropdown ?? true) && <SortDropdown />}
            <CompareToggle />
          </div>
        </div>

        <div className="hfse-form-secondary-fields-right">
          {options.showMap && (
            <button
              type="button"
              className="hk-button hfse-form-map-toggle-btn"
              onClick={toggleMap}
            >
              <span className="hk-icon hfse-form-map-toggle-btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </span>
              <span className="hfse-form-map-toggle-btn-text">
                {state.mapVisible ? 'Hide Map' : 'Show Map'}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
